import { Injectable, Signal, signal } from '@angular/core';

/**
 * Maps dialog/drawer host elements to the close button that overlaps their content slot
 * (`null` while nothing needs avoiding). Ownership is resolved by DOM ancestry instead of DI:
 * slot content is stamped from TemplateRefs declared in the node renderer, so its injector
 * chain bypasses the dialog/drawer component entirely and component-level providers are
 * invisible to it.
 */
@Injectable({ providedIn: 'root' })
export class CloseAffordanceRegistry {
  private readonly scopes = signal<ReadonlyMap<HTMLElement, Signal<HTMLElement | null>>>(new Map());

  /** Registers a dialog/drawer scope; returns the unregister callback. */
  register(scope: HTMLElement, closeButton: Signal<HTMLElement | null>): () => void {
    this.scopes.update((scopes) => new Map(scopes).set(scope, closeButton));
    return () => {
      this.scopes.update((scopes) => {
        const next = new Map(scopes);
        next.delete(scope);
        return next;
      });
    };
  }

  /**
   * Close button of the innermost registered scope containing `host`, or `undefined` when the
   * host is outside every dialog/drawer. Reactive when read in a reactive context: tracks the
   * scope set, and callers tracking the returned signal follow the owner's button changes.
   */
  closeButtonFor(host: HTMLElement): Signal<HTMLElement | null> | undefined {
    let innermostScope: HTMLElement | undefined;
    let innermostButton: Signal<HTMLElement | null> | undefined;
    for (const [scope, closeButton] of this.scopes()) {
      if (scope.contains(host) && (!innermostScope || innermostScope.contains(scope))) {
        innermostScope = scope;
        innermostButton = closeButton;
      }
    }
    return innermostButton;
  }
}
